import type {
	FormControlLabelProps,
	FormControlProps,
	FormGroupProps,
	FormLabelProps,
	RadioGroupProps,
	RadioProps,
	SwitchProps,
	SxProps,
	Theme
} from '@mui/material';
import MUIFormGroup from '@mui/material/FormGroup';
import { useRadioGroup } from '@mui/material/RadioGroup';
import MUISwitch from '@mui/material/Switch';
import type { ReactElement, ReactNode } from 'react';
import { useId } from 'react';
import { ensureArray } from '../../utils/array';
import { Box } from '../box';
import { Card } from '../card';
import { FormControl, FormControlLabel, FormLabel } from '../form-composites/form-control';
import { Icon, type IconName } from '../icon';
import { MuiRadio, MuiRadioGroup } from '../mui-radio';
import { Stack } from '../stack';
import { Typography } from '../typography';

type RichInputListItemContentProps =
	| { title: ReactNode; subtitle?: ReactNode; label?: never }
	| { title?: never; subtitle?: never; label: ReactNode };

type RichInputListItemVisualProps = RichInputListItemContentProps & {
	children?: ReactNode;
	endAdornment?: ReactNode;
	startAdornment?: ReactNode;
	startIcon?: IconName;
	sx?: Exclude<SxProps<Theme>, readonly unknown[]>;
	labelSx?: Exclude<SxProps<Theme>, readonly unknown[]>;
};

type RichInputListItemProps = {
	title?: ReactNode;
	subtitle?: ReactNode;
	label?: ReactNode;
	children?: ReactNode;
	endAdornment?: ReactNode;
	startAdornment?: ReactNode;
	startIcon?: IconName;
	sx?: Exclude<SxProps<Theme>, readonly unknown[]>;
	labelSx?: Exclude<SxProps<Theme>, readonly unknown[]>;
	selected: boolean;
	disabled: boolean;
	control: ReactElement;
	detailsInset: number;
	formControlLabelProps: Omit<FormControlLabelProps, 'children' | 'control' | 'label' | 'sx'>;
};

const RichInputListItem = ({
	title,
	subtitle,
	label,
	children,
	endAdornment,
	startAdornment,
	startIcon,
	sx,
	labelSx,
	selected,
	disabled,
	control,
	detailsInset,
	formControlLabelProps
}: RichInputListItemProps) => {
	const leadingContent = startAdornment ?? (startIcon ? <Icon iconName={startIcon} color={disabled ? 'disabled' : 'inherit'} /> : null);
	const labelContent = label ?? (
		<Stack sx={{ minWidth: 0 }}>
			<Typography variant="body1" fontWeight={500} lineHeight={1.5} color={disabled ? 'text.disabled' : 'text.primary'}>
				{title}
			</Typography>
			{subtitle !== undefined ? (
				<Typography variant="body2" lineHeight={1.2} color={disabled ? 'text.disabled' : 'text.secondary'}>
					{subtitle}
				</Typography>
			) : null}
		</Stack>
	);

	return (
		<Card
			variant="outlined"
			sx={{
				overflow: 'hidden',
				borderRadius: theme => `${theme.shape['radius-md']}px`,
				borderColor: theme => (selected ? theme.palette.primary._states?.focus : theme.palette.elevation.outlined),
				bgcolor: theme =>
					disabled
						? theme.palette.background['paper-elevation-2']
						: selected
							? theme.palette.primary.light
							: theme.palette.background['paper-elevation-3'],
				transition: theme =>
					theme.transitions.create(['background-color', 'border-color'], { duration: theme.transitions.duration.shortest }),
				...(disabled
					? {}
					: {
							'&:hover': {
								borderColor: theme => (selected ? theme.palette.primary._states?.focus : theme.palette.elevation.outlined),
								bgcolor: 'primary.light'
							},
							'&:focus-within': {
								borderColor: theme => theme.palette.primary._states?.focus
							}
						}),
				...(sx ?? {})
			}}
		>
			<FormControlLabel
				{...formControlLabelProps}
				disabled={disabled}
				disableTypography
				control={control}
				label={
					<Stack
						direction="row"
						alignItems="center"
						gap={2}
						sx={{ width: '100%', minWidth: 0, minHeight: '49px', py: '4px', boxSizing: 'border-box' }}
					>
						{leadingContent}
						<Box sx={{ flex: 1, minWidth: 0 }}>{labelContent}</Box>
						{endAdornment !== undefined ? <Box sx={{ flexShrink: 0 }}>{endAdornment}</Box> : null}
					</Stack>
				}
				sx={{
					m: 0,
					px: 2,
					py: 1,
					width: '100%',
					columnGap: 2,
					alignItems: 'center',
					cursor: disabled ? 'default' : 'pointer',
					'& .MuiFormControlLabel-label': {
						flex: 1,
						minWidth: 0
					},
					...(labelSx ?? {})
				}}
			/>
			{children !== undefined ? <Box sx={{ pl: `${detailsInset}px`, pr: 2, pt: 1, pb: 2 }}>{children}</Box> : null}
		</Card>
	);
};

export type RichRadioListProps = Omit<RadioGroupProps, 'children'> & {
	children: ReactNode;
	label?: ReactNode;
	labelProps?: Omit<FormLabelProps, 'children'>;
	formControlProps?: Omit<FormControlProps, 'children'>;
};

export const RichRadioList = ({ children, label, labelProps, formControlProps, sx, ...radioGroupProps }: RichRadioListProps) => {
	const generatedLabelId = useId();
	const labelId = labelProps?.id ?? generatedLabelId;
	const groupSx = ensureArray(sx);

	return (
		<FormControl fullWidth {...formControlProps}>
			{label !== undefined ? (
				<FormLabel {...labelProps} id={labelId}>
					{label}
				</FormLabel>
			) : null}
			<MuiRadioGroup
				{...radioGroupProps}
				aria-labelledby={radioGroupProps['aria-labelledby'] ?? (label !== undefined ? labelId : undefined)}
				sx={[{ gap: 2 }, ...groupSx]}
			>
				{children}
			</MuiRadioGroup>
		</FormControl>
	);
};

export type RichRadioListItemProps = Omit<FormControlLabelProps, 'children' | 'control' | 'label' | 'sx'> &
	RichInputListItemVisualProps & {
		radioProps?: RadioProps;
	};

export const RichRadioListItem = ({
	title,
	subtitle,
	label,
	children,
	endAdornment,
	startAdornment,
	startIcon,
	radioProps,
	sx,
	labelSx,
	value,
	disabled,
	...labelProps
}: RichRadioListItemProps) => {
	const radioGroup = useRadioGroup();
	const selected = radioProps?.checked ?? (radioGroup !== undefined && value !== undefined && String(radioGroup.value) === String(value));
	const itemDisabled = disabled ?? radioProps?.disabled ?? false;
	const hasLeadingContent = startAdornment !== undefined || startIcon !== undefined;

	return (
		<RichInputListItem
			title={title}
			subtitle={subtitle}
			label={label}
			endAdornment={endAdornment}
			startAdornment={startAdornment}
			startIcon={startIcon}
			selected={selected}
			disabled={itemDisabled}
			control={<MuiRadio size="small" {...radioProps} />}
			detailsInset={hasLeadingContent ? 110 : 70}
			formControlLabelProps={{ ...labelProps, value }}
			sx={sx}
			labelSx={labelSx}
		>
			{children}
		</RichInputListItem>
	);
};

export type RichSwitchListProps = Omit<FormGroupProps, 'children'> & {
	children: ReactNode;
	label?: ReactNode;
	labelProps?: Omit<FormLabelProps, 'children'>;
	formControlProps?: Omit<FormControlProps, 'children'>;
};

export const RichSwitchList = ({ children, label, labelProps, formControlProps, sx, ...formGroupProps }: RichSwitchListProps) => {
	const generatedLabelId = useId();
	const labelId = labelProps?.id ?? generatedLabelId;
	const groupSx = ensureArray(sx);

	return (
		<FormControl fullWidth {...formControlProps}>
			{label !== undefined ? (
				<FormLabel {...labelProps} id={labelId}>
					{label}
				</FormLabel>
			) : null}
			<MUIFormGroup
				{...formGroupProps}
				role={formGroupProps.role ?? 'group'}
				aria-labelledby={formGroupProps['aria-labelledby'] ?? (label !== undefined ? labelId : undefined)}
				sx={[{ gap: 2 }, ...groupSx]}
			>
				{children}
			</MUIFormGroup>
		</FormControl>
	);
};

const RichSwitchThumb = ({ checked }: { checked: boolean }) => (
	<Box
		sx={{
			width: 20,
			height: 20,
			borderRadius: '50%',
			bgcolor: 'background.paper-elevation-2',
			boxShadow: 1,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center'
		}}
	>
		<Icon iconName={checked ? 'Check' : 'Close'} sx={{ fontSize: 16, color: 'primary.main' }} />
	</Box>
);

export type RichSwitchListItemProps = Omit<FormControlLabelProps, 'checked' | 'children' | 'control' | 'label' | 'sx'> &
	RichInputListItemVisualProps & {
		checked: boolean;
		switchProps?: Omit<SwitchProps, 'checked' | 'disabled' | 'onChange'>;
	};

export const RichSwitchListItem = ({
	title,
	subtitle,
	label,
	children,
	endAdornment,
	startAdornment,
	startIcon,
	checked,
	switchProps,
	sx,
	labelSx,
	disabled,
	...labelProps
}: RichSwitchListItemProps) => {
	const itemDisabled = disabled ?? false;
	const switchSx = ensureArray(switchProps?.sx);
	const hasLeadingContent = startAdornment !== undefined || startIcon !== undefined;

	return (
		<RichInputListItem
			title={title}
			subtitle={subtitle}
			label={label}
			endAdornment={endAdornment}
			startAdornment={startAdornment}
			startIcon={startIcon}
			selected={checked}
			disabled={itemDisabled}
			control={
				<MUISwitch
					{...switchProps}
					color={switchProps?.color ?? 'primary'}
					size={switchProps?.size ?? 'medium'}
					inputProps={{ ...switchProps?.inputProps, role: 'switch' }}
					icon={switchProps?.icon ?? <RichSwitchThumb checked={false} />}
					checkedIcon={switchProps?.checkedIcon ?? <RichSwitchThumb checked />}
					sx={[
						{
							width: 64,
							height: 38,
							p: 0,
							'& .MuiSwitch-switchBase': {
								p: '9px',
								'&.Mui-checked': {
									transform: 'translateX(26px)',
									'& + .MuiSwitch-track': {
										opacity: 1,
										bgcolor: 'primary.main',
										borderColor: 'primary._states.outlinedBorder'
									}
								},
								'&.Mui-disabled + .MuiSwitch-track': {
									opacity: 1,
									bgcolor: 'action.disabledBackground'
								}
							},
							'& .MuiSwitch-track': {
								position: 'absolute',
								left: 7,
								top: 7,
								width: 50,
								height: 24,
								boxSizing: 'border-box',
								border: 1,
								borderColor: 'elevation.outlined',
								borderRadius: theme => `${theme.shape['radius-md']}px`,
								opacity: 1,
								bgcolor: theme => theme.palette._components.switch.slideFill
							}
						},
						...switchSx
					]}
				/>
			}
			detailsInset={hasLeadingContent ? 56 : 16}
			formControlLabelProps={{ ...labelProps, checked, labelPlacement: 'start' }}
			sx={sx}
			labelSx={labelSx}
		>
			{children}
		</RichInputListItem>
	);
};
